import { useEffect, useState } from "react";
import {
  authUpdatedEvent,
  sessionKey,
  tokenRefreshFinishedEvent,
} from "../Constants/authConfig";
import { BrowserStorage } from "../utils/browserStorage";

type AuthSession = Record<string, any> | null;

const parseAuthSession = (): AuthSession => {
  try {
    const authObject = BrowserStorage.getSessionItem(sessionKey);
    if (!authObject) return null;
    return JSON.parse(authObject);
  } catch {
    return null;
  }
};

export const useAuthSession = () => {
  const [authSession, setAuthSession] = useState<AuthSession>(() =>
    parseAuthSession(),
  );

  useEffect(() => {
    const syncAuthSession = () => {
      setAuthSession(parseAuthSession());
    };

    const syncOnStorage = (event: StorageEvent) => {
      if (event?.key === sessionKey) {
        syncAuthSession();
      }
    };

    syncAuthSession();
    window.addEventListener(authUpdatedEvent, syncAuthSession);
    window.addEventListener(tokenRefreshFinishedEvent, syncAuthSession);
    window.addEventListener("focus", syncAuthSession);
    window.addEventListener("storage", syncOnStorage);

    return () => {
      window.removeEventListener(authUpdatedEvent, syncAuthSession);
      window.removeEventListener(tokenRefreshFinishedEvent, syncAuthSession);
      window.removeEventListener("focus", syncAuthSession);
      window.removeEventListener("storage", syncOnStorage);
    };
  }, []);

  return authSession;
};

export const useIdToken = (): string | null => {
  const authSession = useAuthSession();
  return authSession?.idToken ?? null;
};
